import React, { useState } from 'react';
import { Card, Table, Button, Space, Tag, Typography, Spin, Alert, Modal, Form, Input, InputNumber, Select, Switch, message, Statistic, Row, Col, Tabs, DatePicker, Radio } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, GiftOutlined, TrophyOutlined, HistoryOutlined, SendOutlined } from '@ant-design/icons';
import { useAuth } from '../../hooks/useAuth';
import {
    useGetAdminRewardTiersQuery,
    useCreateAdminRewardTierMutation,
    useUpdateAdminRewardTierMutation,
    useDeleteAdminRewardTierMutation,
    useGetAdminRewardEarningsQuery,
    useGetAdminRewardRedemptionsQuery,
    useGetAdminRewardStatsQuery,
    useGrantAdminRewardMutation,
    useGetAdminOrganizationsQuery,
    IRewardTier,
    IRewardEarning,
    IRewardRedemption,
    RewardType,
    RewardStatus,
} from '../../services/nestjs-base-api';

const { Title, Text } = Typography;
const { TextArea } = Input;

export const AdminRewardsPage: React.FC = () => {
    const { user } = useAuth();
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isGrantModalOpen, setIsGrantModalOpen] = useState(false);
    const [editingTier, setEditingTier] = useState<IRewardTier | null>(null);
    const [grantType, setGrantType] = useState<'tier' | 'custom'>('tier');
    const [form] = Form.useForm();
    const [grantForm] = Form.useForm();

    const { data: tiers, isLoading: loadingTiers, error: tiersError } = useGetAdminRewardTiersQuery({
        jwtToken: user?.jwtToken || '',
    });

    const { data: earnings, isLoading: loadingEarnings } = useGetAdminRewardEarningsQuery({
        jwtToken: user?.jwtToken || '',
    });

    const { data: redemptions, isLoading: loadingRedemptions } = useGetAdminRewardRedemptionsQuery({
        jwtToken: user?.jwtToken || '',
    });

    const { data: stats } = useGetAdminRewardStatsQuery({
        jwtToken: user?.jwtToken || '',
    });

    const { data: organisationsData } = useGetAdminOrganizationsQuery({
        jwtToken: user?.jwtToken || '',
        limit: 1000, // Get all orgs for the dropdown
    });

    const [createTier, { isLoading: isCreating }] = useCreateAdminRewardTierMutation();
    const [updateTier, { isLoading: isUpdating }] = useUpdateAdminRewardTierMutation();
    const [deleteTier] = useDeleteAdminRewardTierMutation();
    const [grantReward, { isLoading: isGranting }] = useGrantAdminRewardMutation();

    const handleCreate = () => {
        setEditingTier(null);
        form.resetFields();
        form.setFieldsValue({
            isActive: true,
            displayOrder: 0,
            rewardType: 'credits',
        });
        setIsCreateModalOpen(true);
    };

    const handleEdit = (tier: IRewardTier) => {
        setEditingTier(tier);
        form.setFieldsValue({
            code: tier.code,
            name: tier.name,
            description: tier.description,
            minResponses: tier.minResponses,
            maxResponses: tier.maxResponses,
            rewardValue: tier.rewardValue,
            rewardType: tier.rewardType,
            isActive: tier.isActive,
            displayOrder: tier.displayOrder,
        });
        setIsCreateModalOpen(true);
    };

    const handleDelete = (tier: IRewardTier) => {
        Modal.confirm({
            title: 'Delete Reward Tier?',
            content: `Are you sure you want to delete "${tier.name}"? This action cannot be undone.`,
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    await deleteTier({
                        jwtToken: user?.jwtToken || '',
                        id: tier.id,
                    }).unwrap();
                    message.success('Reward tier deleted successfully');
                } catch (error: any) {
                    message.error(error?.data?.message || 'Failed to delete reward tier');
                }
            },
        });
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            if (editingTier) {
                await updateTier({
                    jwtToken: user?.jwtToken || '',
                    id: editingTier.id,
                    payload: values,
                }).unwrap();
                message.success('Reward tier updated successfully');
            } else {
                await createTier({
                    jwtToken: user?.jwtToken || '',
                    payload: values,
                }).unwrap();
                message.success('Reward tier created successfully');
            }

            setIsCreateModalOpen(false);
            form.resetFields();
        } catch (error: any) {
            message.error(error?.data?.message || (editingTier ? 'Failed to update reward tier' : 'Failed to create reward tier'));
        }
    };

    const handleOpenGrantModal = () => {
        grantForm.resetFields();
        grantForm.setFieldsValue({
            responsesCollected: 0,
            customRewardType: 'credits',
        });
        setGrantType('tier');
        setIsGrantModalOpen(true);
    };

    const handleGrantReward = async () => {
        try {
            const values = await grantForm.validateFields();

            const payload: any = {
                organisationId: values.organisationId,
                responsesCollected: values.responsesCollected || 0,
            };

            if (grantType === 'tier') {
                payload.tierId = values.tierId;
            } else {
                payload.customRewardValue = values.customRewardValue;
                payload.customRewardType = values.customRewardType;
                payload.customRewardName = values.customRewardName;
            }

            if (values.period && values.period.length === 2) {
                payload.earningPeriodStart = values.period[0].toISOString();
                payload.earningPeriodEnd = values.period[1].toISOString();
            }

            await grantReward({
                jwtToken: user?.jwtToken || '',
                payload,
            }).unwrap();

            message.success('Reward granted successfully!');
            setIsGrantModalOpen(false);
            grantForm.resetFields();
            setGrantType('tier');
        } catch (error: any) {
            message.error(error?.data?.message || 'Failed to grant reward');
        }
    };

    const getRewardTypeTag = (type: RewardType) => {
        const colors: Record<RewardType, string> = {
            credits: 'blue',
            discount_percentage: 'purple',
            account_credit_cents: 'cyan',
        };
        const labels: Record<RewardType, string> = {
            credits: 'Credits',
            discount_percentage: 'Discount %',
            account_credit_cents: 'Account Credit',
        };
        return <Tag color={colors[type]}>{labels[type]}</Tag>;
    };

    const getStatusTag = (status: RewardStatus) => {
        const colors: Record<RewardStatus, string> = {
            pending: 'orange',
            available: 'green',
            redeemed: 'blue',
            expired: 'red',
            forfeited: 'default',
        };
        return <Tag color={colors[status]}>{status.toUpperCase()}</Tag>;
    };

    const formatValue = (value: number, type: RewardType) => {
        switch (type) {
            case 'credits':
                return `${value} credits`;
            case 'discount_percentage':
                return `${value}%`;
            case 'account_credit_cents':
                return `$${(value / 100).toFixed(2)}`;
            default:
                return value;
        }
    };

    const tierColumns = [
        {
            title: 'Code',
            dataIndex: 'code',
            key: 'code',
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Response Range',
            key: 'range',
            render: (_: any, record: IRewardTier) => (
                <Text>
                    {record.minResponses} - {record.maxResponses || 'unlimited'}
                </Text>
            ),
        },
        {
            title: 'Reward',
            key: 'reward',
            render: (_: any, record: IRewardTier) => (
                <Space>
                    {getRewardTypeTag(record.rewardType)}
                    <Text strong>{formatValue(record.rewardValue, record.rewardType)}</Text>
                </Space>
            ),
        },
        {
            title: 'Status',
            key: 'status',
            render: (_: any, record: IRewardTier) => (
                <Tag color={record.isActive ? 'green' : 'red'}>
                    {record.isActive ? 'Active' : 'Inactive'}
                </Tag>
            ),
        },
        {
            title: 'Order',
            dataIndex: 'displayOrder',
            key: 'displayOrder',
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: IRewardTier) => (
                <Space>
                    <Button
                        size="small"
                        icon={<EditOutlined style={{ marginRight: 4 }} />}
                        onClick={() => handleEdit(record)}
                    >
                        Edit
                    </Button>
                    <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined style={{ marginRight: 4 }} />}
                        onClick={() => handleDelete(record)}
                    >
                        Delete
                    </Button>
                </Space>
            ),
        },
    ];

    // Helper to get organisation name from ID
    const getOrganisationName = (orgId: number) => {
        const org = organisationsData?.data?.find((o: any) => o.id === orgId);
        return org?.name || `Org #${orgId}`;
    };

    const earningColumns = [
        {
            title: 'Organisation',
            key: 'organisationId',
            render: (_: any, record: IRewardEarning) => (
                <Text>{getOrganisationName(Number(record.organisationId))}</Text>
            ),
        },
        {
            title: 'Tier',
            key: 'tier',
            render: (_: any, record: IRewardEarning) => (
                <Text>{record.rewardTier?.name || (record.rewardTierId ? `Tier #${record.rewardTierId}` : 'Custom Reward')}</Text>
            ),
        },
        {
            title: 'Period',
            key: 'period',
            render: (_: any, record: IRewardEarning) => (
                <Text>
                    {new Date(record.earningPeriodStart).toLocaleDateString()} - {new Date(record.earningPeriodEnd).toLocaleDateString()}
                </Text>
            ),
        },
        {
            title: 'Responses',
            dataIndex: 'responsesCollected',
            key: 'responsesCollected',
        },
        {
            title: 'Value',
            key: 'value',
            render: (_: any, record: IRewardEarning) => (
                <Text strong>{formatValue(record.rewardValue, record.rewardType)}</Text>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: RewardStatus) => getStatusTag(status),
        },
        {
            title: 'Created',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date: string) => new Date(date).toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
            }),
        },
    ];

    const redemptionColumns = [
        {
            title: 'Organisation',
            key: 'organisationId',
            render: (_: any, record: IRewardRedemption) => (
                <Text>{getOrganisationName(record.organisationId)}</Text>
            ),
        },
        {
            title: 'Type',
            dataIndex: 'redemptionType',
            key: 'redemptionType',
            render: (type: string) => {
                const labels: Record<string, string> = {
                    credits: 'Credits',
                    subscription_discount: 'Subscription Discount',
                    account_credit: 'Account Credit',
                };
                return <Tag color="blue">{labels[type] || type}</Tag>;
            },
        },
        {
            title: 'Total Value',
            dataIndex: 'totalValue',
            key: 'totalValue',
        },
        {
            title: 'Details',
            key: 'details',
            render: (_: any, record: IRewardRedemption) => {
                if (record.creditsGranted) return `${record.creditsGranted} credits`;
                if (record.discountPercentage) return `${record.discountPercentage}% discount`;
                if (record.accountCreditCents) return `$${(record.accountCreditCents / 100).toFixed(2)}`;
                return '-';
            },
        },
        {
            title: 'Redeemed By',
            dataIndex: 'redeemedBy',
            key: 'redeemedBy',
        },
        {
            title: 'Redeemed At',
            dataIndex: 'redeemedAt',
            key: 'redeemedAt',
            render: (date: string) => new Date(date).toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
            }),
        },
    ];

    if (loadingTiers || loadingEarnings || loadingRedemptions) {
        return (
            <div style={{ padding: '24px', textAlign: 'center' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (tiersError) {
        return (
            <div style={{ padding: '24px' }}>
                <Alert
                    message="Error loading reward data"
                    description="Unable to fetch rewards. Please try again later."
                    type="error"
                    showIcon
                />
            </div>
        );
    }

    const tabItems = [
        {
            key: 'tiers',
            label: (
                <span>
                    <TrophyOutlined style={{ marginRight: 8 }} />
                    Reward Tiers
                </span>
            ),
            children: (
                <Card>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                        <Title level={4} style={{ margin: 0 }}>
                            Reward Tiers
                        </Title>
                        <Button
                            type="primary"
                            icon={<PlusOutlined style={{ marginRight: 4 }} />}
                            onClick={handleCreate}
                        >
                            Create Tier
                        </Button>
                    </div>
                    <Table
                        dataSource={tiers || []}
                        columns={tierColumns}
                        rowKey="id"
                        pagination={false}
                    />
                </Card>
            ),
        },
        {
            key: 'earnings',
            label: (
                <span>
                    <GiftOutlined style={{ marginRight: 8 }} />
                    Earnings ({earnings?.length || 0})
                </span>
            ),
            children: (
                <Card>
                    <Title level={4} style={{ marginBottom: 16 }}>
                        Reward Earnings
                    </Title>
                    <Table
                        dataSource={earnings || []}
                        columns={earningColumns}
                        rowKey="id"
                        pagination={{ pageSize: 20 }}
                    />
                </Card>
            ),
        },
        {
            key: 'redemptions',
            label: (
                <span>
                    <HistoryOutlined style={{ marginRight: 8 }} />
                    Redemptions ({redemptions?.length || 0})
                </span>
            ),
            children: (
                <Card>
                    <Title level={4} style={{ marginBottom: 16 }}>
                        Reward Redemptions
                    </Title>
                    <Table
                        dataSource={redemptions || []}
                        columns={redemptionColumns}
                        rowKey="id"
                        pagination={{ pageSize: 20 }}
                    />
                </Card>
            ),
        },
    ];

    return (
        <div style={{ padding: '24px' }}>
            <Card style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                    <Title level={3} style={{ margin: 0 }}>
                        <GiftOutlined style={{ marginRight: 8 }} />
                        Rewards Management
                    </Title>
                    <Button
                        type="primary"
                        icon={<SendOutlined style={{ marginRight: 4 }} />}
                        onClick={handleOpenGrantModal}
                    >
                        Grant Reward
                    </Button>
                </div>

                {stats && (
                    <Row gutter={16}>
                        <Col span={6}>
                            <Statistic title="Active Tiers" value={stats.activeTiers} suffix={`/ ${stats.totalTiers}`} />
                        </Col>
                        <Col span={6}>
                            <Statistic title="Available Earnings" value={stats.availableEarnings} valueStyle={{ color: '#52c41a' }} />
                        </Col>
                        <Col span={6}>
                            <Statistic title="Pending Earnings" value={stats.pendingEarnings} valueStyle={{ color: '#faad14' }} />
                        </Col>
                        <Col span={6}>
                            <Statistic title="Total Redeemed" value={stats.totalRedemptions} />
                        </Col>
                    </Row>
                )}
            </Card>

            <Tabs items={tabItems} />

            <Modal
                title={editingTier ? 'Edit Reward Tier' : 'Create Reward Tier'}
                open={isCreateModalOpen}
                onOk={handleSubmit}
                onCancel={() => {
                    setIsCreateModalOpen(false);
                    form.resetFields();
                }}
                confirmLoading={isCreating || isUpdating}
                width={600}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="code"
                        label="Code"
                        rules={[{ required: true, message: 'Code is required' }]}
                    >
                        <Input
                            placeholder="e.g., bronze_tier"
                            disabled={!!editingTier}
                        />
                    </Form.Item>

                    <Form.Item
                        name="name"
                        label="Name"
                        rules={[{ required: true, message: 'Name is required' }]}
                    >
                        <Input placeholder="e.g., Bronze Tier" />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="Description"
                    >
                        <TextArea rows={3} />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="minResponses"
                                label="Min Responses"
                                rules={[{ required: true, message: 'Min responses is required' }]}
                            >
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="maxResponses"
                                label="Max Responses"
                                extra="Leave empty for unlimited"
                            >
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="rewardType"
                                label="Reward Type"
                                rules={[{ required: true }]}
                            >
                                <Select>
                                    <Select.Option value="credits">Credits</Select.Option>
                                    <Select.Option value="discount_percentage">Discount Percentage</Select.Option>
                                    <Select.Option value="account_credit_cents">Account Credit (cents)</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="rewardValue"
                                label="Reward Value"
                                rules={[{ required: true, message: 'Reward value is required' }]}
                            >
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="displayOrder"
                                label="Display Order"
                                initialValue={0}
                            >
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="isActive"
                                label="Active"
                                valuePropName="checked"
                                initialValue={true}
                            >
                                <Switch />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>

            {/* Grant Reward Modal */}
            <Modal
                title={
                    <Space>
                        <SendOutlined />
                        <span>Grant Reward to Organisation</span>
                    </Space>
                }
                open={isGrantModalOpen}
                onOk={handleGrantReward}
                onCancel={() => {
                    setIsGrantModalOpen(false);
                    grantForm.resetFields();
                }}
                confirmLoading={isGranting}
                okText="Grant Reward"
                width={600}
            >
                <Alert
                    message="Manual Reward Grant"
                    description="This will immediately create an available reward earning for the selected organisation. Use this for special rewards, promotions, or corrections."
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
                <Form form={grantForm} layout="vertical">
                    <Form.Item
                        name="organisationId"
                        label="Organisation"
                        rules={[{ required: true, message: 'Please select an organisation' }]}
                    >
                        <Select
                            showSearch
                            placeholder="Select an organisation"
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                                (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {organisationsData?.data?.map((org: any) => (
                                <Select.Option key={org.id} value={org.id}>
                                    {org.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item label="Reward Type">
                        <Radio.Group
                            value={grantType}
                            onChange={(e) => setGrantType(e.target.value)}
                        >
                            <Radio.Button value="tier">Use Existing Tier</Radio.Button>
                            <Radio.Button value="custom">Custom One-Off</Radio.Button>
                        </Radio.Group>
                    </Form.Item>

                    {grantType === 'tier' ? (
                        <Form.Item
                            name="tierId"
                            label="Reward Tier"
                            rules={[{ required: grantType === 'tier', message: 'Please select a reward tier' }]}
                        >
                            <Select placeholder="Select a reward tier">
                                {tiers?.filter(t => t.isActive).map((tier) => (
                                    <Select.Option key={tier.id} value={tier.id}>
                                        {tier.name} - {formatValue(tier.rewardValue, tier.rewardType)}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    ) : (
                        <>
                            <Form.Item
                                name="customRewardName"
                                label="Reward Name"
                                rules={[{ required: grantType === 'custom', message: 'Please enter a reward name' }]}
                            >
                                <Input placeholder="e.g., Promotional Bonus, Service Credit, Beta Tester Reward" />
                            </Form.Item>

                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        name="customRewardType"
                                        label="Reward Type"
                                        rules={[{ required: grantType === 'custom' }]}
                                    >
                                        <Select>
                                            <Select.Option value="credits">Credits</Select.Option>
                                            <Select.Option value="discount_percentage">Discount %</Select.Option>
                                            <Select.Option value="account_credit_cents">Account Credit (cents)</Select.Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="customRewardValue"
                                        label="Reward Value"
                                        rules={[{ required: grantType === 'custom', message: 'Please enter a value' }]}
                                    >
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </>
                    )}

                    <Form.Item
                        name="responsesCollected"
                        label="Responses Collected (Optional)"
                        extra="Number of responses this reward is being granted for (for record keeping)"
                    >
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                        name="period"
                        label="Earning Period (Optional)"
                        extra="The date range this reward covers. Defaults to current date if not specified."
                    >
                        <DatePicker.RangePicker style={{ width: '100%' }} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};
